/* eslint-disable no-magic-numbers */
import Drawer from 'components/Drawer/Drawer';
import Modal from 'components/Modal/Modal';
import dayjs, { Dayjs } from 'dayjs';
import { MobileOpCode, Visitor } from 'graphql/generated/graphqlTypes';
import useAccessEvents from 'hooks/useAccessEvents/useAccessEvents';
import useAccessEventsFilters from 'hooks/useAccessEventsFilters/useAccessEventsFilters';
import useDoors from 'hooks/useDoors/useDoors';
import useDrawer from 'hooks/useDrawer/useDrawer';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import useExportAccessEvents from 'hooks/useExportAccessEvents/useExportAccessEvents';
import useModal from 'hooks/useModal/useModal';
import useTranslation, { Translation } from 'hooks/useTranslation/useTranslation';
import useVisitorsGroupsMembers from 'hooks/useVisitorsGroupsMembers/useVisitorsGroupsMembers';
import { kebabCase, startCase, upperFirst } from 'lodash';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import Chip from 'ui/atoms/Chip/Chip';
import Icon from 'ui/atoms/Icon/Icon';
import Loader from 'ui/atoms/Loader/Loader';
import TableCell from 'ui/atoms/TableCell/TableCell';
import TableHeader from 'ui/atoms/TableHeader/TableHeader';
import Tooltip from 'ui/atoms/Tooltip/Tooltip';
import TooltipInfo from 'ui/atoms/TooltipInfo/TooltipInfo';
import Typography from 'ui/atoms/Typography/Typography';
import FilterNoResult from 'ui/molecules/FilterNoResult/FilterNoResult';
import TableHeaderRow from 'ui/molecules/TableHeaderRow/TableHeaderRow';
import TableLoader from 'ui/molecules/TableLoader/TableLoader';
import TableRow from 'ui/molecules/TableRow/TableRow';
import Table from 'ui/organisms/Table/Table';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';
import PageContent from 'ui/templates/PageContent/PageContent';
import { StickyToTop } from 'ui/templates/StickyToTop/StickyToTop';
import { getTranslatedReason } from 'utils/AccessEvent/AccessEvent';
import ArrayUtil from 'utils/Array/Array.util';
import { createEventsTabs } from '../eventsTabsConst';

const IconWrapper = styled.div`
  cursor: pointer;
  height: 1.5rem;
`;

const TableWrapper = styled.div`
  z-index: 0;
  height: calc(100vh - 24rem);
  width: calc(100vw - 25rem);
  margin-right: 1rem;
`;

const FilterNoResultWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: calc((100vh - 31.59rem) / 2);
`;

interface SomeProps {
  isNextDataToDownload: boolean;
}

const TableLoaderWrapper = styled.div<SomeProps>`
  ${({ isNextDataToDownload }) =>
    !isNextDataToDownload &&
    `
      display: none;
    `};
`;

export const createColumns = (translation: Translation) =>
  [
    { text: translation.date_time, width: 10 },
    { text: translation.user, width: 35 },
    { text: translation.lock, width: 15 },
    { text: translation.action, width: 15 },
    { text: translation.reason, width: 25 }
  ] as const;

const AccessEvents: React.FC<React.PropsWithChildren> = () => {
  const { translation, getLanguage, getLocaleForDayjs } = useTranslation();
  const columns = createColumns(translation);
  const eventsTabs = createEventsTabs(translation);
  const myRef = useRef<null | HTMLDivElement>(null);
  const [loaderIsVisible, setLoaderIsVisible] = useState<boolean>(false);
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });
  const {
    accessEventFilters,
    removeDateTimeAppliedFilter,
    removeLockAppliedFilter,
    removeVisitorAppliedFilter,
    removeActionAppliedFilter,
    removeReasonAppliedFilter,
    isFilterApply
  } = useAccessEventsFilters();
  const { visitorsGroupsMembers } = useVisitorsGroupsMembers({
    handleFetchError
  });
  const { doors } = useDoors({
    handleFetchError
  });
  const {
    accessEvents,
    isNextDataToDownload,
    loading: accessEventsLoading
  } = useAccessEvents({
    handleFetchError,
    fetchNextData: loaderIsVisible,
    filter: accessEventFilters.appliedFilters
  });
  const { isDownloadInProgress } = useExportAccessEvents({
    handleFetchError
  });
  const { showDrawer } = useDrawer();
  const { showModal } = useModal();
  const skeletonArray = ArrayUtil.SkeletonArray();

  const filterIconOnClick = () => {
    showDrawer({
      type: 'accessEventsFilters',
      contentValue: { filters: accessEventFilters.appliedFilters }
    });
  };

  const exportIconOnClick = () => {
    if (!isDownloadInProgress) {
      if (isFilterApply())
        showModal({
          type: 'createExportAccessEvents'
        });
      else showModal({ type: 'preparingExportAccessEvents', contentValue: { isFilteredOptionSelected: false } });
    }
  };

  const getMemberNameById = () => {
    const members: Array<Visitor> = [];
    visitorsGroupsMembers.forEach((group) => members.push(...group.members));
    return members.find(
      (member) =>
        accessEventFilters.appliedFilters.visitorIds && member.id === accessEventFilters.appliedFilters.visitorIds[0]
    )?.name;
  };

  const getLockNameById = () =>
    doors.find(
      (lock) => accessEventFilters.appliedFilters.doorIds && lock.id === accessEventFilters.appliedFilters.doorIds[0]
    )?.name;

  const getTranslatedFormattedDate = (date: Dayjs) => {
    const translatedDate = date.locale(getLocaleForDayjs(getLanguage));
    return `${translatedDate.format('D')} ${upperFirst(translatedDate.format('MMM'))} ${translatedDate.format('YYYY')}`;
  };

  const observer = new IntersectionObserver((entries) => {
    const entry = entries[0];
    setLoaderIsVisible(entry.isIntersecting);
  });

  if (myRef.current) observer.observe(myRef.current);

  return (
    <PageContent title="ATLAS" titleId="location" tabs={eventsTabs} id="access-events-page">
      <StickyToTop>
        <ComponentWrapper justifyContent="space-between" width="100%">
          <ComponentWrapper
            justifyContent="flex-start"
            alignItems="flex-start"
            columnGap="0.25rem"
            rowGap="0.25rem"
            flexWrap="wrap"
          >
            {isFilterApply() && (
              <ComponentWrapper justifyContent="center" gap="0.25rem">
                <ComponentWrapper margin="0 0.25rem 0 0">
                  <Typography overflow="">Filter:</Typography>
                </ComponentWrapper>
                {accessEventFilters.appliedFilters.dateRange && (
                  <Chip
                    key="access-events-date-time-chip"
                    title={`${translation.date}/${translation.time}`}
                    value={`${dayjs(accessEventFilters.appliedFilters.dateRange.rangeStart).format(
                      'D MMM HH:mm'
                    )} - ${dayjs(accessEventFilters.appliedFilters.dateRange.rangeEnd).format('D MMM HH:mm')}`}
                    id="access-events-date-time-chip"
                    deleteOnClick={() => removeDateTimeAppliedFilter()}
                  />
                )}
                {accessEventFilters.appliedFilters.visitorIds && (
                  <Chip
                    key="access-events-user-chip"
                    title={`${translation.user}:`}
                    value={`${getMemberNameById()}`}
                    id="access-events-user-chip"
                    deleteOnClick={() => removeVisitorAppliedFilter()}
                  />
                )}
                {accessEventFilters.appliedFilters.doorIds && (
                  <Chip
                    key="access-events-door-chip"
                    title={`${translation.lock}:`}
                    value={`${getLockNameById()}`}
                    id="access-events-door-chip"
                    deleteOnClick={() => removeLockAppliedFilter()}
                  />
                )}
                {accessEventFilters.appliedFilters.actions && (
                  <Chip
                    key="access-events-actions-chip"
                    title={`${translation.action}:`}
                    value={startCase(`${accessEventFilters.appliedFilters.actions}`.replaceAll('_', ' ').toLowerCase())}
                    id="access-events-actions-chip"
                    deleteOnClick={() => removeActionAppliedFilter()}
                  />
                )}
                {accessEventFilters.appliedFilters.reason && (
                  <Chip
                    key="access-events-reason-chip"
                    title={`${translation.reason}:`}
                    value={`${getTranslatedReason(accessEventFilters.appliedFilters.reason, translation)}`}
                    id="access-events-reason-chip"
                    deleteOnClick={() => removeReasonAppliedFilter()}
                  />
                )}
              </ComponentWrapper>
            )}
          </ComponentWrapper>
          <ComponentWrapper alignItems="center" justifyContent="center" gap="0.5rem">
            <IconWrapper>
              <Icon
                name="Filter"
                color={isFilterApply() ? 'primary' : 'lTextMedium'}
                id="filter-icon"
                onClick={filterIconOnClick}
                viewBox="0 0 24 24"
              />
            </IconWrapper>

            {isDownloadInProgress ? (
              <Loader />
            ) : (
              <Tooltip
                elementOnHover={
                  <ComponentWrapper width="100%" padding="0.5rem" id="test">
                    <Typography variant="body3">{translation.export_to_file}</Typography>
                  </ComponentWrapper>
                }
                id="access-events-export-tooltip"
              >
                <IconWrapper>
                  <Icon
                    name="Export"
                    color="lTextMedium"
                    id="access-events-exportIcon"
                    onClick={exportIconOnClick}
                    viewBox="0 0 24 24"
                  />
                </IconWrapper>
              </Tooltip>
            )}
            <Tooltip
              elementOnHover={<TooltipInfo firstLineText={translation.the_retention_time_is_30_days} />}
              id="tooltip"
            >
              <IconWrapper>
                <Icon name="Info" id="i-icon" />
              </IconWrapper>
            </Tooltip>
          </ComponentWrapper>
        </ComponentWrapper>
      </StickyToTop>
      <TableWrapper>
        <Table
          header={
            <TableHeaderRow>
              {columns.map((column) => (
                <TableHeader
                  headerText={column.text}
                  flex={`1 0 ${column.width}%`}
                  id={kebabCase(`access-events-${column.text}`)}
                />
              ))}
            </TableHeaderRow>
          }
        >
          {accessEvents.length === 0 && isFilterApply() && (
            <FilterNoResultWrapper>
              <FilterNoResult />
            </FilterNoResultWrapper>
          )}
          {accessEvents.length === 0 &&
            accessEventsLoading &&
            skeletonArray?.map((_, index) => (
              <TableRow key={`skeletonTableRow-${_.id}`} id={`row-skeleton-${index}`}>
                <TableCell isLoading firstLineText="" flex={`1 0 ${columns[0].width}%`} />
                <TableCell isLoading firstLineText="" flex={`1 0 ${columns[1].width}%`} />
                <TableCell isLoading firstLineText="" flex={`1 0 ${columns[2].width}%`} />
                <TableCell isLoading firstLineText="" flex={`1 0 ${columns[3].width}%`} />
                <TableCell isLoading firstLineText="" flex={`1 0 ${columns[4].width}%`} />
              </TableRow>
            ))}
          {accessEvents.length > 0 &&
            accessEvents?.map((accessEvent, index) => (
              <TableRow key={`accessEvent-${accessEvent.id}`} hoverEffect id={`row-${index}`}>
                <TableCell
                  firstLineText={dayjs(accessEvent.createdAt).locale(getLocaleForDayjs(getLanguage)).format('HH:mm:ss')}
                  firstLineId={`row-${index}-time`}
                  secondLineText={getTranslatedFormattedDate(dayjs(accessEvent.createdAt))}
                  secondLineId={`row-${index}-date`}
                  flex={`1 0 ${columns[0].width}%`}
                />
                <TableCell
                  secondLineText={accessEvent.visitor.email}
                  secondLineId={`row-${index}-email`}
                  firstLineText={accessEvent.visitor.name}
                  firstLineId={`row-${index}-name`}
                  flex={`1 0 ${columns[1].width}%`}
                />
                <TableCell
                  firstLineText={accessEvent.door.name}
                  firstLineId={`row-${index}-door`}
                  flex={`1 0 ${columns[2].width}%`}
                />
                <TableCell
                  firstLineText={
                    accessEvent.action === MobileOpCode.AccessGranted
                      ? `${translation.access_granted}`
                      : `${translation.access_denied}`
                  }
                  firstLineId={`row-${index}-action`}
                  firstLineColor={accessEvent.action === MobileOpCode.AccessGranted ? 'success' : 'error'}
                  flex={`1 0 ${columns[3].width}%`}
                />
                <TableCell
                  firstLineText={getTranslatedReason(accessEvent.reason || '-', translation)}
                  firstLineId={`row-${index}-reason`}
                  flex={`1 0 ${columns[4].width}%`}
                />
              </TableRow>
            ))}
          <TableLoaderWrapper isNextDataToDownload={isNextDataToDownload}>
            <TableRow key="access-events-get-more-data-loader" hoverEffect>
              <TableLoader ref={myRef}>
                <Loader id="access-events-get-more-data-loader" />
              </TableLoader>
            </TableRow>
          </TableLoaderWrapper>
        </Table>
      </TableWrapper>
      <Drawer />
      <Modal />
    </PageContent>
  );
};

export default AccessEvents;
